import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUserProfile } from '../../hooks/useUserProfile';
import logo from '../../assets/todayover-withicon.svg';

export default function Topbar() {
  const { user, signOut } = useAuth();
  const { profile } = useUserProfile(user?.id);
  const firstName = profile?.display_name?.split(' ')[0] || null;
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }

    function handleEscapeKey(event) {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
      }
    }

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [showProfileMenu]);

  // Helper to check if nav item is active
  const isNavActive = (path, additionalPaths = []) => {
    const pathname = window.location.pathname;
    if (pathname === path) return true;
    return additionalPaths.some(p => pathname.includes(p));
  };

  return (
    <div className="sticky top-0 z-30 bg-background px-3 sm:px-6 shadow-sm backdrop-blur-[8px] border-b-2 border-primary-15">
      {/* Accent bar at top */}
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{
          background: 'var(--accent-primary)',
        }}
      />

      <div className="max-w-[1400px] mx-auto flex items-center justify-between h-[56px] sm:h-[70px]">
        {/* Logo - fixed width to balance with right section for centered nav */}
        <div className="w-auto lg:w-[200px]">
          <NavLink
            to="/dashboard"
            className="transition-all duration-300 hover:scale-105"
          >
            <img src={logo} alt="Today Over Yesterday" className="h-10 sm:h-12 pt-1" />
          </NavLink>
        </div>

        {/* Desktop Nav - centered */}
        <nav className="hidden md:flex gap-2 flex-1 justify-center" aria-label="Main navigation">
          {[
            { to: '/dashboard', label: 'Dashboard', paths: [] },
            { to: '/workouts', label: 'Workouts', paths: ['/workouts'] },
            { to: '/daily', label: 'Daily', paths: ['/daily', '/nutrition', '/supplements', '/daily-entry'] },
            { to: '/wearables', label: 'Wearables', paths: [] },
            { to: '/social', label: 'Social', paths: ['/social', '/gyms'] },
            { to: '/roast', label: 'Roast', paths: ['/roast'] },
          ].map(({ to, label, paths }) => (
            <NavLink key={to} to={to}>
              {({ isActive }) => {
                const active = isActive || isNavActive(to, paths);
                return (
                  <div
                    className={`relative py-2 px-4 font-heading text-[0.9rem] font-light uppercase tracking-[0.5px] no-underline transition-all duration-300 cursor-pointer hover:text-primary hover:-translate-y-0.5 ${
                      active ? 'text-primary' : 'text-foreground-muted'
                    }`}
                  >
                    {label}
                    {active && (
                      <div
                        className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-sm bg-primary"
                      />
                    )}
                  </div>
                );
              }}
            </NavLink>
          ))}
        </nav>

        {/* Right Section: Notifications + Profile - fixed width to balance with logo */}
        <div className="w-auto lg:w-[200px] flex items-center justify-end gap-2 lg:gap-4">
          {/* Notification Bell */}
          <button
            className="relative p-2 bg-transparent border-none cursor-pointer rounded-lg transition-all duration-300 hover:bg-primary-10 group"
            onClick={() => {/* TODO: Open notifications */}}
            aria-label="Notifications"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="stroke-foreground-muted group-hover:stroke-primary group-hover:scale-110 transition-all duration-300"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {/* Notification Badge */}
            <div
              className="absolute top-1 right-1 w-2 h-2 rounded-full border-2 border-background bg-error"
            />
          </button>

          {/* User Profile */}
          <div ref={profileMenuRef} className="relative">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 py-1 px-2 bg-primary-5 border border-primary-20 rounded-xl cursor-pointer transition-all duration-300 hover:bg-primary-10 hover:border-primary-30 hover:-translate-y-0.5"
              aria-label="User menu"
              aria-expanded={showProfileMenu}
              aria-haspopup="true"
            >
              {/* Avatar */}
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-base text-primary-foreground uppercase bg-primary"
              >
                {firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
              {/* Username - hidden on mobile */}
              <div className="hidden md:flex flex-col items-start">
                <div className="font-heading text-[0.85rem] font-semibold text-foreground uppercase tracking-[0.5px]">
                  {firstName || user?.email?.split('@')[0] || 'User'}
                </div>
              </div>
              {/* Dropdown Arrow */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={`hidden md:block stroke-foreground-muted transition-transform duration-300 ${
                  showProfileMenu ? 'rotate-180' : 'rotate-0'
                }`}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>

            {/* Profile Dropdown Menu */}
            {showProfileMenu && (
              <div className="absolute top-[calc(100%+8px)] right-0 min-w-[200px] bg-background border-2 border-primary-20 rounded-xl shadow-lg overflow-hidden z-50" role="menu" aria-label="User menu">
                <div className="p-3 border-b border-border">
                  <div className="text-xs text-foreground-muted mb-1">
                    SIGNED IN AS
                  </div>
                  <div className="font-heading text-[0.9rem] font-semibold text-foreground">
                    {user?.email}
                  </div>
                </div>

                <NavLink
                  to="/settings"
                  className="block p-3 text-foreground no-underline text-[0.9rem] font-medium transition-all duration-200 hover:bg-primary-5"
                  onClick={() => setShowProfileMenu(false)}
                  role="menuitem"
                >
                  Settings
                </NavLink>

                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    signOut();
                  }}
                  className="w-full p-3 bg-transparent border-none border-t border-border text-error text-left text-[0.9rem] font-medium cursor-pointer transition-all duration-200 hover:bg-error-10"
                  role="menuitem"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
